import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { Box, ImageX } from '@bridebook/ui';
import { Button, ButtonProps } from '@bridebook/aura';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import {
  useRsvpData,
  useRsvpForms,
  useRsvpNavigation,
  useRsvpState,
} from '../../guest-rsvp.context';
import componentStyles from '../substep.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';
import getWebsiteUrl from '@bridebook/web/lib/rsvp/utils/get-website-url';
import { useRouter } from 'next/router';
import SubmitButtonDesktopWrapper from '../../partials/submit-button-desktop-wrapper';
import SubmitButtonMobileWrapper from '../../partials/submit-button-mobile-wrapper';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { getGuestRsvpStatus, sortRelatedGuests } from '@bridebook/web/app-basic/lib/rsvp/utils';
import { PartyMemberRow } from '../../partials/party-member-row';

interface IBackToWebsiteButtonProps {
  href: string;
  children: string;
  variant?: ButtonProps['variant'];
}

const BackToWebsiteButton: React.FC<IBackToWebsiteButtonProps> = ({
  href,
  children,
  variant = 'primary',
}) => (
  <Link href={href}>
    <Button variant={variant}>{children}</Button>
  </Link>
);

const ConfirmationStep: React.FC = () => {
  const { t } = useTranslation('weddingWebsite');
  const router = useRouter();
  const { weddingWebsite, theme, relatedGuests } = useRsvpData();
  const { websiteId, isPreview, setRsvpSubmissionStatus } = useRsvpState();
  const { setStep, setCurrentQuestionIndex } = useRsvpNavigation();
  const { nameForm, eventsForm, questionsForm } = useRsvpForms();

  const handleStartRsvpForMember = useCallback(
    (memberName: string, memberId: string) => {
      nameForm.reset({ fullName: memberName });
      eventsForm.reset();
      questionsForm.reset();
      setCurrentQuestionIndex(0);
      setRsvpSubmissionStatus('NOT_RESOLVED');
      setStep('NAME_FORM');
      router.replace(
        { query: { ...router.query, guest: memberName, guestId: memberId } },
        undefined,
        { shallow: true },
      );
    },
    [
      nameForm,
      eventsForm,
      questionsForm,
      setCurrentQuestionIndex,
      setRsvpSubmissionStatus,
      setStep,
      router,
    ],
  );

  const partners = weddingWebsite.wedding.partners;
  const guestName = relatedGuests[0]?.name ?? '';

  const otherRelatedGuests = useMemo(
    () => sortRelatedGuests(relatedGuests.slice(1)),
    [relatedGuests],
  );

  const hasRelatedGuests = otherRelatedGuests.length > 0;
  const hasActionablePrimaryButtons = otherRelatedGuests.some(
    (member) => getGuestRsvpStatus(member.invitation) === 'pending',
  );
  const backButtonVariant = hasRelatedGuests && hasActionablePrimaryButtons ? 'ghost' : 'primary';

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);
  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  const websiteUrl = getWebsiteUrl(websiteId);

  return (
    <>
      <Box style={styles.substepWrapper}>
        <Box as="h2" style={styles.confirmation.title}>
          <Trans
            ns="weddingWebsite"
            i18nKey="rsvp.guest.confirmation.titleWithName"
            values={{ guestName, partners }}>
            {'{{guestName, possessive}} response has been sent '}
            <br />
            {'to {{partners.0}} & {{partners.1}}'}
          </Trans>
        </Box>
        <Box style={styles.confirmation.image}>
          <ImageX
            src={`${IMAGES_BASE_URL}/assets/wedding-websites-imgs/rsvp_success.png`}
            w={120}
            h={120}
          />
        </Box>
        {hasRelatedGuests && (
          <>
            <Box style={styles.confirmation.partyDescription}>
              {t('rsvp.guest.confirmation.partyDescription')}
            </Box>
            <Box style={styles.confirmation.partyList}>
              {otherRelatedGuests.map((member) => (
                <PartyMemberRow
                  key={member.id}
                  member={member}
                  websiteId={websiteId}
                  onStartRsvp={handleStartRsvpForMember}
                />
              ))}
            </Box>
          </>
        )}
        <SubmitButtonDesktopWrapper>
          <BackToWebsiteButton href={websiteUrl} variant={backButtonVariant}>
            {t('rsvp.guest.confirmation.cta.backToWebsite')}
          </BackToWebsiteButton>
        </SubmitButtonDesktopWrapper>
      </Box>
      <SubmitButtonMobileWrapper>
        <BackToWebsiteButton href={websiteUrl} variant={backButtonVariant}>
          {t('rsvp.guest.confirmation.cta.backToWebsite')}
        </BackToWebsiteButton>
      </SubmitButtonMobileWrapper>
    </>
  );
};

export default ConfirmationStep;
